import * as React from "react";
import {authorize, IWithRouterProps, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {
    FalconNavigatorHeader,
    ICacheManager,
    ICustomerPortalService,
    PolicyProperty,
    ProductsEnum,
    Types
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {ChangeCardForm} from "./ChangeCardForm";
import {Navigate} from "react-router";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {getProductByPolicyNumber} from "../../../../../CommonFunctions";

interface IParams {
    policyNumber: string
    paymentReference: string
    [key: string]: string | undefined
}

interface IState {
    addCardSuccess: boolean
    addCardCancelled: boolean
    adHocPaymentsAllowed: boolean
    verificationReceived: boolean
    isAnnualProduct: boolean
    isAnnualPayment: boolean
    redirectToRenewal: boolean
    directRedirect: string | null
    renewalInteractionId?: string | null
    reuseCard: boolean
}

@authorize
export class ChangeCardPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {
    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;
    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;
    private readonly product: ProductsEnum;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.product = getProductByPolicyNumber(this.props.params.policyNumber);

        this.state = {
            verificationReceived: false,
            addCardSuccess: false,
            addCardCancelled: false,
            adHocPaymentsAllowed: sessionStorage.getItem("Falcon_adHocPaymentsAllowed") === 'true',
            isAnnualProduct: this.product !== ProductsEnum.SUBSCRIPTION,
            isAnnualPayment: false,
            redirectToRenewal: sessionStorage.getItem("redirectJourney") === "renewal",
            directRedirect: sessionStorage.getItem("directRedirectAfterCardSave"),
            renewalInteractionId:  sessionStorage.getItem("renewalInteraction") !== null ? sessionStorage.getItem("renewalInteraction"): "",
            reuseCard: sessionStorage.getItem("reuseCard") === 'true'
        };

        this.verifyNewCard = this.verifyNewCard.bind(this);
        this.verificationNeeded = this.verificationNeeded.bind(this);
    }

    public async componentDidMount() {
        this.pluginTemplates.setPageTitle("Change your payment card");
        this.cacheManager.clearCache("getPolicies");

        if (this.verificationNeeded()) {
            this.verifyNewCard(this.props.params.paymentReference);
        }

        this.customerPortalService.getPolicyVersionPropertiesByPropertyKeys(this.props.params.policyNumber, ["premiumType"]).subscribe(
            (properties: PolicyProperty[]) => {
                properties.forEach(property => {
                    if (property.key === "premiumType") {
                        this.setState({
                            isAnnualPayment: property.value === "annual"
                        })
                    }
                })
            }
        )
    }

    public render() {
        const {
            addCardSuccess,
            addCardCancelled,
            verificationReceived
        } = this.state;

        const annualPaymentPage: string = this.state.isAnnualPayment ? '/dashboard/annualPaymentHistory/' + this.props.params.policyNumber : `/dashboard/monthlyPaymentHistory/` + this.props.params.policyNumber;

        const annualRedirectPage: string = this.state.redirectToRenewal ? `/journey/show/product/AnnualMotorInsurance/process/renewal/viewAndEditRenewalProposalPage/${this.state.renewalInteractionId}` : this.state.directRedirect ?? annualPaymentPage;

        if (this.state.redirectToRenewal) {
            window.sessionStorage.setItem("renewalCardWasChanged", "true");
        }

        if ((addCardSuccess || addCardCancelled) ) {
            if (addCardSuccess) {
                window.sessionStorage.setItem("showPopup", "true");
                window.sessionStorage.setItem("referrerJourney", "addNewCard");
            }
            sessionStorage.removeItem("directRedirectAfterCardSave");
            this.cacheManager.clearCache("getProfile");
            return <Navigate to={`${this.state.isAnnualProduct ? annualRedirectPage : '/dashboard/paymentHistory'}`}/>
        }

        // ensures there are no needless calls to payment gateway
        const showForm: boolean = !this.verificationNeeded() || !addCardSuccess;

        return (
            <React.Fragment>
                <FalconHeader/>
                <div className="plugin">
                    <FalconNavigatorHeader
                        back={`${this.state.isAnnualProduct ? annualPaymentPage : '/dashboard/account/cardConsent'}`}
                        title="Change your payment card"
                    />
                    { showForm &&
                        <ChangeCardForm
                            adHocPaymentsAllowed={this.state.adHocPaymentsAllowed}
                            verifyNewCardFailed={this.verificationNeeded() && verificationReceived && !addCardSuccess}
                            policyNumber={this.props.params.policyNumber}
                            />
                    }
                    {this.pluginTemplates.renderBackReloadLink("Back", false, `${this.state.isAnnualProduct ? annualPaymentPage : '/dashboard/account/cardConsent'}`)}
                </div>
                <FalconFooter/>
            </React.Fragment>
        );
    }

    private async verifyNewCard(paymentReference: string): Promise<void> {
        const reuseCardStatus: boolean = this.state.isAnnualProduct ? this.state.reuseCard : this.state.adHocPaymentsAllowed;
        const observable = await this.customerPortalService
            .verifyNewCard(paymentReference, reuseCardStatus, this.state.isAnnualProduct, this.props.params.policyNumber)
            .toPromise();

        this.setState({
            addCardSuccess: observable && observable.successful,
            addCardCancelled: observable && observable.cancelled,
            verificationReceived: true
        })
    }

    private verificationNeeded(): boolean {
        return Boolean(this.props.params.paymentReference);
    }
}

export const ChangeCardPage = withRouter<{}, IParams>(ChangeCardPagePlain)
